import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { authService } from '../../../services/supabaseService';
import {
  getPasswordChecks,
  isPasswordValid,
  validatePassword,
} from '../../../utils/passwordRules';
import { sendPasswordChangedEmail } from '../../../utils/sendPasswordChangedEmail';

const ChangePasswordModal = ({ isOpen, onClose, user }) => {
  const [passwordForm, setPasswordForm] = useState({
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setPasswordForm({ password: '', confirmPassword: '' });
    setShowPassword(false);
    setPasswordError('');
    setPasswordSuccess('');
  }, [isOpen, user?.id]);

  if (!isOpen) return null;

  const passwordChecks = getPasswordChecks(passwordForm.password);

  const handlePasswordChange = (field, value) => {
    setPasswordForm((prev) => ({ ...prev, [field]: value }));
    if (passwordError) setPasswordError('');
    if (passwordSuccess) setPasswordSuccess('');
  };

  const handleClose = () => {
    if (isSavingPassword) return;
    onClose?.();
  };

  const handleSavePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    const validationError = validatePassword(
      passwordForm.password,
      passwordForm.confirmPassword
    );
    if (validationError) {
      setPasswordError(validationError);
      return;
    }
    if (!user?.id) {
      setPasswordError('Utilisateur introuvable');
      return;
    }
    if (!user?.email) {
      setPasswordError('Email utilisateur manquant : impossible d’envoyer la notification');
      return;
    }

    setIsSavingPassword(true);
    try {
      const { error } = await authService.updateUserPassword(user.id, passwordForm.password);
      if (error) {
        throw new Error(error.message || 'Impossible de changer le mot de passe');
      }

      const newPassword = passwordForm.password;
      setPasswordForm({ password: '', confirmPassword: '' });
      setShowPassword(false);

      try {
        await sendPasswordChangedEmail({
          email: user.email,
          password: newPassword,
          fullName: user.name || user.full_name || '',
        });
        setPasswordSuccess(
          'Mot de passe mis à jour. Un email avec les nouveaux identifiants a été envoyé.'
        );
      } catch (emailErr) {
        console.error('Erreur envoi email mot de passe:', emailErr);
        setPasswordSuccess('Mot de passe mis à jour.');
        setPasswordError(
          emailErr?.message ||
            'Le mot de passe a été changé, mais l’email n’a pas pu être envoyé.'
        );
      }
    } catch (err) {
      console.error('Erreur changement mot de passe:', err);
      setPasswordError(err?.message || 'Impossible de changer le mot de passe');
    } finally {
      setIsSavingPassword(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[400] p-4">
      <div
        className="bg-card rounded-lg border border-border w-full max-w-md shadow-lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-password-title"
      >
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <h2 id="change-password-title" className="text-lg font-semibold text-foreground">
              Changer le mot de passe
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {user?.email || user?.name || 'Utilisateur'}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            disabled={isSavingPassword}
            aria-label="Fermer"
          >
            <Icon name="X" size={18} />
          </Button>
        </div>

        <div className="p-4 space-y-3">
          <p className="text-xs text-muted-foreground">
            Un email avec les nouveaux identifiants sera envoyé à l&apos;utilisateur.
          </p>

          <div className="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              label="Nouveau mot de passe"
              value={passwordForm.password}
              onChange={(e) => handlePasswordChange('password', e.target.value)}
              placeholder="Nouveau mot de passe"
              autoComplete="new-password"
              disabled={isSavingPassword}
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute right-3 top-9 text-muted-foreground hover:text-foreground"
              disabled={isSavingPassword}
              aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            >
              <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
            </button>
          </div>

          <ul className="space-y-1.5 rounded-lg border border-border bg-muted/30 p-3">
            {passwordChecks.map((check) => (
              <li
                key={check.id}
                className={`flex items-center gap-2 text-xs ${
                  check.ok ? 'text-success' : 'text-muted-foreground'
                }`}
              >
                <Icon name={check.ok ? 'CheckCircle' : 'Circle'} size={14} />
                <span>{check.label}</span>
              </li>
            ))}
          </ul>

          <Input
            type={showPassword ? 'text' : 'password'}
            label="Confirmer le mot de passe"
            value={passwordForm.confirmPassword}
            onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
            placeholder="Confirmer le mot de passe"
            autoComplete="new-password"
            disabled={isSavingPassword}
          />

          {passwordForm.confirmPassword &&
            passwordForm.password !== passwordForm.confirmPassword && (
              <p className="text-xs text-error">Les mots de passe ne correspondent pas</p>
            )}

          {passwordError && <p className="text-sm text-error">{passwordError}</p>}
          {passwordSuccess && <p className="text-sm text-success">{passwordSuccess}</p>}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleClose}
            disabled={isSavingPassword}
          >
            Fermer
          </Button>
          <Button
            type="button"
            variant="default"
            size="sm"
            iconName="Key"
            iconPosition="left"
            loading={isSavingPassword}
            onClick={handleSavePassword}
            disabled={
              isSavingPassword ||
              !isPasswordValid(passwordForm.password) ||
              passwordForm.password !== passwordForm.confirmPassword
            }
          >
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
